import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import LoginPage from './components/LoginPage';
import DeltaTrianglePage from './components/DeltaTrianglePage';
import TodoBoardPage from './components/TodoBoardPage';
import GithubAuthCallback from './components/GithubAuthCallback';
import RepoConsole from './components/RepoConsole';
import AdvisorConsole from './components/AdvisorConsole';
import PortalHomePage from './components/PortalHomePage';
import MyAIPage from './components/MyAIPage';
import './App.css';

// Protected Route component
const ProtectedRoute = ({ children, user }) => {
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Authentication listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      console.log('Auth state changed:', currentUser?.uid ? `User: ${currentUser.uid}` : 'No user');
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div className="app">
        <div className="loading-container">
          <svg width="60" height="60" viewBox="0 0 200 200" className="loading-logo">
            <defs>
              <linearGradient id="loadingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#4A90E2" />
                <stop offset="50%" stopColor="#357ABD" />
                <stop offset="100%" stopColor="#2E5B9A" />
              </linearGradient>
            </defs>
            <polygon 
              points="100,20 170,160 30,160" 
              fill="url(#loadingGradient)" 
              stroke="#4A90E2" 
              strokeWidth="2"
              opacity="0.8"
            />
          </svg>
          <div className="loading-text">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Login route - accessible to everyone */}
        <Route 
          path="/login" 
          element={
            user ? <Navigate to="/" replace /> : <LoginPage />
          } 
        />
        
        {/* Protected app route */}
        <Route
          path="/app"
          element={
            <ProtectedRoute user={user}>
              <DeltaTrianglePage />
            </ProtectedRoute>
          }
        />

        {/* Protected board route */}
        <Route
          path="/board"
          element={
            <ProtectedRoute user={user}>
              <TodoBoardPage />
            </ProtectedRoute>
          }
        />

        {/* My AI — legacy Glass Editor personal journal + coach (legacy top-level Firebase paths) */}
        <Route
          path="/my-ai"
          element={
            <ProtectedRoute user={user}>
              <MyAIPage />
            </ProtectedRoute>
          }
        />

        {/* Strategic Advisor S2S console — Firebase-auth gated (reads users/{uid} memory). */}
        <Route
          path="/advisor"
          element={
            <ProtectedRoute user={user}>
              <AdvisorConsole />
            </ProtectedRoute>
          }
        />

        {/* Voice repo coder — local agent backend. Gated by AGENT_TOKEN, not Firebase. */}
        <Route path="/coder" element={<RepoConsole />} />

        {/* GitHub OAuth callback */}
        <Route path="/oauth/github/callback" element={<GithubAuthCallback />} />

        {/* Portal homepage - Business vs Central Development. Signed-out users are sent to /login by ProtectedRoute. */}
        <Route
          path="/"
          element={
            <ProtectedRoute user={user}>
              <PortalHomePage />
            </ProtectedRoute>
          }
        />
        
        {/* Catch all route */}
        <Route 
          path="*" 
          element={<Navigate to="/" replace />} 
        />
      </Routes>
    </Router>
  );
}

export default App;